import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../api";
import {
    QuestionConnectOption,
    QuestionLanguage,
    QuestionOpenOption,
    QuestionOrderOption,
    QuestionSelectOption,
    GridRow, Image, QuestionScaleOption
} from "../../../types/admin";
import { changeGameDetail } from "../adminGameView";
import { ModalState } from "../../../types/modals";
import { toastSuccess } from "../admin";


export type QuestionEditModalState = ModalState & {
    languageTab: string
    content_id: string | null
    section_id: string | null
    type: string
    languages: QuestionLanguage[]
    multiple: boolean
    shuffle: boolean
    layout: string
    time_limit: number
    select_options: QuestionSelectOption[]
    order_options: QuestionOrderOption[]
    connect_options: QuestionConnectOption[]
    text_options: QuestionOpenOption[]
    rating_type: string
    min_scale: number
    max_scale: number
    number_scale_options: QuestionScaleOption[]
    allow_skip: boolean
    only_title: boolean
    slide_rows: GridRow[]
    language_image: Image | null
    default_image: Image | null
    points: number
    isUploading: boolean
}

const initialState: QuestionEditModalState = {
    loading: false,
    isOpen: false,
    languageTab: '',
    error: null,
    content_id: null,
    section_id: null,
    type: '',
    languages: [],
    multiple: false,
    shuffle: true,
    layout: 'default',
    time_limit: 0,
    select_options: [],
    order_options: [],
    connect_options: [],
    text_options: [],
    rating_type: 'default',
    min_scale: 1,
    max_scale: 5,
    number_scale_options: [],
    allow_skip: false,
    only_title: false,
    slide_rows: [],
    language_image: null,
    default_image: null,
    points: 0,
    isUploading: false
}

type QuestionModalPayload = {
    section_id: string,
    content_id: string | null,
    language: string,
    type: string
}

type QuestionGetPayload = {
    game_id: string
    content_id: string
    version_id: string
}

export type NewQuestionPayload = {
    game_id: string
    version_id: string
    section_id: string
    type: string
    languages: QuestionLanguage[]
    select_options?: QuestionSelectOption[]
    order_options?: QuestionOrderOption[]
    connect_options?: QuestionConnectOption[]
    text_options?: QuestionOpenOption[]
    min_scale?: number
    max_scale?: number
    points?: number
    multiple?: boolean
    shuffle?: boolean
    layout?: string
    time_limit?: number
    number_scale_options?: QuestionScaleOption[]
    rating_type?: string
    allow_skip?: boolean
    keepOpen?: boolean
}

export type EditQuestionPayload = NewQuestionPayload & {
    content_id: string
}

export type TranslateQuestionPayload = EditQuestionPayload & {
    translate_to: string
    translate_from: string
}

export const translateQuestion = createAsyncThunk(
    'questionEditModal/translateQuestions',
    async (payload: TranslateQuestionPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/questions/${content_id}/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getQuestion = createAsyncThunk(
    'questionEditModal/getQuestion',
    async (payload: QuestionGetPayload, thunkAPI) => {
        const { content_id, version_id, game_id } = payload;
        try {
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/questions/${content_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getSlide = createAsyncThunk(
    'questionEditModal/getSlide',
    async (payload: QuestionGetPayload, thunkAPI) => {
        const { content_id, version_id, game_id } = payload;
        try {
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/slides/${content_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getElement = createAsyncThunk(
    'questionEditModal/getElement',
    async (payload: QuestionGetPayload, thunkAPI) => {
        const { content_id, version_id, game_id } = payload;
        try {
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/elements/${content_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewQuestion = createAsyncThunk(
    'questionEditModal/saveNewQuestion',
    async (payload: NewQuestionPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, type } = payload;

        const isScale = type === 'number_scale';

        try {
            const response = await api.post(`/API/admin/games/${game_id}/versions/${version_id}/questions/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return { ...response, isScale };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type NewSlidePayload = {
    type: string,
    game_id: string,
    version_id: string,
    section_id: string,
    languages: QuestionLanguage[],
    only_title: boolean
}

export const saveNewSlide = createAsyncThunk(
    'questionEditModal/saveNewSlide',
    async (payload: NewSlidePayload, thunkAPI) => {
        const { game_id, version_id } = payload;
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post(`/API/admin/games/${game_id}/versions/${version_id}/slides/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


type EditSlidePayload = NewSlidePayload & {
    content_id: string
}

type TranslateSlidePayload = EditSlidePayload & {
    slide_rows: GridRow[]
    translate_to: string
    translate_from: string
}

export const translateSlide = createAsyncThunk(
    'questionEditModal/translateSlide',
    async (payload: TranslateSlidePayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/slides/${content_id}/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveSlide = createAsyncThunk(
    'questionEditModal/saveSlide',
    async (payload: EditSlidePayload, thunkAPI) => {
        const { game_id, version_id, content_id } = payload;
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/slides/${content_id}/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const translateElement = createAsyncThunk(
    'questionEditModal/translateElement',
    async (payload: FormData, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const content_id = payload.get('content_id');
        try {
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/elements/${content_id}/`, payload, true);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveElement = createAsyncThunk(
    'questionEditModal/saveElement',
    async (payload: FormData, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const content_id = payload.get('content_id');
        const baseUrl = `/API/admin/games/${game_id}/versions/${version_id}/elements`;

        try {
            const response = await api[content_id ? 'patch' : 'post'](
                `${baseUrl}${content_id ? `/${content_id}/` : '/'}`,
                payload,
                true
            );

            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }

            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }

            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const saveQuestion = createAsyncThunk(
    'questionEditModal/saveQuestion',
    async (payload: EditQuestionPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/questions/${content_id}/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            if (response.version_sections) {
                dispatch(changeGameDetail({ version_sections: response.version_sections }));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type QuestionDeletePayload = {
    game_id: string
    content_id: string
    version_id: string
}

export const deleteQuestion = createAsyncThunk(
    'questionEditModal/deleteQuestion',
    async (payload: QuestionDeletePayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.delete(`/API/admin/games/${game_id}/versions/${version_id}/questions/${content_id}/`);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(changeGameDetail({ version_sections: response.version_sections }));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveQuestionImage = createAsyncThunk(
    'questionEditModal/saveQuestionImage',
    async (payload: FormData, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const content_id = payload.get('content_id');

        try {
            const response = await api.post(`/API/admin/games/${game_id}/versions/${version_id}/questions/${content_id}/image/`, payload, true);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteSectionElement = createAsyncThunk(
    'questionEditModal/deleteSectionElement',
    async (payload: QuestionDeletePayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.delete(`/API/admin/games/${game_id}/versions/${version_id}/elements/${content_id}/`);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(changeGameDetail({ version_sections: response.version_sections }));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const deleteSlide = createAsyncThunk(
    'questionEditModal/deleteSlide',
    async (payload: QuestionDeletePayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const { game_id, version_id, content_id } = payload;
        try {
            const response = await api.delete(`/API/admin/games/${game_id}/versions/${version_id}/slides/${content_id}/`);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(changeGameDetail({ version_sections: response.version_sections }));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


type slideRowsPayload = {
    content_id: string
    slide_rows: GridRow[]
}

export const saveSlideRows = createAsyncThunk(
    'questionEditModal/saveSlideRows',
    async (payload: slideRowsPayload, thunkAPI) => {
        const { content_id } = payload;
        try {
            const response = await api.patch(`/API/admin/slides/${content_id}/rows/`, payload);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type deleteElementPayload = {
    content_id: string
    element_id: string
}

export const deleteElement = createAsyncThunk(
    'elementModal/deleteElement',
    async (payload: deleteElementPayload, thunkAPI) => {
        const { content_id, element_id } = payload;
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.delete(`/API/admin/slides/${content_id}/elements/${element_id}/`);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


type deleteRowPayload = {
    content_id: string
    row_id: string
}

export const deleteRow = createAsyncThunk(
    'elementModal/deleteRow',
    async (payload: deleteRowPayload, thunkAPI) => {
        const { content_id, row_id } = payload;
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.delete(`/API/admin/slides/${content_id}/rows/${row_id}/`);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


const slice = createSlice({
    name: 'questionEditModal',
    initialState,
    reducers: {
        openQuestionModal: (state, action: PayloadAction<QuestionModalPayload>) => {
            if (action.payload.content_id) {
                state.content_id = action.payload.content_id;
            }
            if (action.payload.section_id) {
                state.section_id = action.payload.section_id;
            }
            state.type = action.payload.type;
            state.languageTab = action.payload.language;

            state.isOpen = true;
        },
        setActiveLanguage: (state, action: PayloadAction<string>) => {
            state.languageTab = action.payload;
        },
        closeQuestionModal: () => {
            return initialState;
        },
        changeQuestionModal: (state, action: PayloadAction<Partial<QuestionEditModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getElement.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveElement.fulfilled, (state, action) => {
                return initialState;
            })
            .addCase(saveElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteRow.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteRow.fulfilled, (state, action) => {
                state.slide_rows = action.payload.slide_rows;
                state.loading = false;
            })
            .addCase(deleteRow.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteElement.fulfilled, (state, action) => {
                state.slide_rows = action.payload.slide_rows;
                state.loading = false;
            })
            .addCase(deleteElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveSlideRows.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveSlideRows.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.slide_rows = action.payload.slide_rows;
            })
            .addCase(saveSlideRows.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getSlide.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSlide.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getSlide.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getQuestion.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getQuestion.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteSlide.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSlide.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteSlide.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteQuestion.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteQuestion.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteSectionElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSectionElement.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteSectionElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewQuestion.fulfilled, (state, action) => {
                if (!action.payload.isScale && !action.meta.arg.keepOpen) {
                    return initialState;
                }

                state.loading = false;
                state.error = null;
                if (action.payload.content_id) {
                    state.content_id = action.payload.content_id.toString();
                }
                if (action.payload.isScale) {
                    Object.assign(state, action.payload);
                }
            })
            .addCase(saveNewQuestion.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveQuestion.fulfilled, (state, action) => {
                if (action.meta.arg.keepOpen) {
                    state.loading = false;
                    state.error = null;
                } else {
                    return initialState;
                }
            })
            .addCase(saveQuestion.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewSlide.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewSlide.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(saveNewSlide.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveSlide.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveSlide.fulfilled, () => {
                return initialState;
            })
            .addCase(saveSlide.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(translateElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(translateElement.fulfilled, (state, action) => {
                state.languages = action.payload.languages;
                state.loading = false;
                state.error = null;
            })
            .addCase(translateElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(translateSlide.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(translateSlide.fulfilled, (state, action) => {
                state.languages = action.payload.languages;
                state.slide_rows = action.payload.slide_rows;
                state.loading = false;
                state.error = null;
            })
            .addCase(translateSlide.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })

            .addCase(translateQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(translateQuestion.fulfilled, (state, action) => {
                state.languages = action.payload.languages;
                state.select_options = action.payload.select_options;
                state.connect_options = action.payload.connect_options;
                state.order_options = action.payload.order_options;
                state.text_options = action.payload.text_options;
                state.loading = false;
                state.error = null;
            })
            .addCase(translateQuestion.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveQuestionImage.pending, (state) => {
                state.isUploading = true;
                state.error = null;
            })
            .addCase(saveQuestionImage.fulfilled, (state) => {
                state.isUploading = false;
                state.error = null;
            })
            .addCase(saveQuestionImage.rejected, (state, action) => {
                state.isUploading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    openQuestionModal,
    closeQuestionModal,
    changeQuestionModal,
    setActiveLanguage,
} = slice.actions;
export default slice.reducer;
